import { faAdd, faCalendar, faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "react-router-dom";
import PageLayout from "../components/PageLayout";
import ChaseRenderRow from "../components/chaseListComponents/chaseRenderRow";
import { chaseHeadingData } from "../components/chaseListComponents/chaseListData";
import { useEffect, useRef, useState } from "react";
import axiosInstance from "../utils/axiosInstance";
import TableWrapper from "../components/TableWrapper";
import { useDebounceValue } from "usehooks-ts";
import formQueryUrl from "../utils/formQueryUrl";
import PaginationMenu from "../components/pagination/paginationMenu";
import GetUser from "../utils/getUser";
import TopBarWidget from "../components/TopDashboardWidget";
import DateRangePicker from "../components/date-range-picker";
import { useRangePicker } from "../store/use-range-picker-model";
import { useChaseMenu } from "../store/use-chase-menu";
import CustomSelect from "../components/CustomSelect";
import fetchChaseDetail from "../utils/fetchChaseDetail";
import DebtSyncAlert from "../components/dashboard/debt-sync-alert";

const chargeFilterOptions = [
  { value: "newest", label: "Newest" },
  { value: "oldest", label: "Oldest" },
];

const ChaseList = () => {
  const currentUser = GetUser();
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const location = window.location.pathname;
  const myCutomers = window.location.pathname === "/my-customers";
  const customerArchive = window.location.pathname === "/customer-archives";
  const chaseStatus = window.location.pathname.split("/")[2] || "";
  const { onToggleChaseMenu, onChaseMenuClose } = useChaseMenu();
  const [totalCount, setTotalCount] = useState<number>(0);
  const [pageSize] = useState<number>(10);
  const [page, setPage] = useState<number>(1);
  const [query, setQuery] = useDebounceValue("", 1000);
  const [chase, setChase] = useState<Record<string, any>[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [dateRange, setDateRange] = useState<Record<string, any>>({});
  const { onToggleRangePicker } = useRangePicker();
  const searchQuery = new URLSearchParams(window.location.search).get("query");
  const [isSameCustomerChase, setIsSameCustomerChase] = useState<string | null>(
    null
  );
  const [chargefilter, setChargeFilter] = useState<string>("oldest");

  // A search is a look at one customer, so the whole picture is worth loading up
  // front. Browsing the list is not — those rows fetch their detail on expand.
  const isSearching = Boolean(query?.trim());

  // ---------------------------------------- [ FIND CHASES ] -------------------------------
  useEffect(() => {
    const getChaseList = async () => {
      setLoading(true);
      const filtersArray = [
        { search: query },
        { status: chaseStatus },
        myCutomers && { schemeCode: currentUser?.code },
        chargefilter && { chargebackFilter: chargefilter },
        customerArchive && { status: "Customer-Archives" },
        dateRange?.startDate !== null && { startDate: dateRange?.startDate },
        dateRange?.endDate !== null && { endDate: dateRange?.endDate },
        !isSearching && { lean: true },
      ];
      onChaseMenuClose();
      try {
        const res = await axiosInstance.get(
          `/loan?page=${page}&limit=${pageSize}${formQueryUrl(filtersArray)}`
        );
        setChase(res.data.data);
        setTotalCount(res.data.totalCount);
        setIsSameCustomerChase(res.data.isSameCustomerChase);
        setLoading(false);
      } catch (error) {
        console.log(error);
        setLoading(false);
      }
    };

    getChaseList();
  }, [
    query,
    chaseStatus,
    page,
    pageSize,
    myCutomers,
    currentUser?.code,
    dateRange,
    customerArchive,
    chargefilter,
  ]);

  // -------------------------------- [ UPDATE CURRENT PAGE ] --------------------------

  const OnChangeCurrentPage = (page) => {
    setPage(page);
  };

  // -------------------------------- [ NAVIGATE TO USER ] --------------------------------

  const handleClick = () => {
    navigate("/createChase");
  };

  // ------------------------------- [ SEARCH BASED ON URL QUERY ] --------------------

  useEffect(() => {
    const updateQuery = () => {
      if (searchQuery && searchRef.current) {
        searchRef.current.value = searchQuery;
        setQuery(searchQuery);
        navigate(window.location.pathname);
      }
    };

    currentUser?.id && updateQuery();
  }, [searchQuery, navigate, setQuery, searchRef, currentUser?.id]);

  // --------------------------------- [ IF JUST ONE CHASE ITEM OPEN THAT UP ] -----------

  useEffect(() => {
    if (chase.length !== 1) {
      return;
    }

    let ignore = false;
    const item = chase[0];

    const openOnlyChase = async () => {
      // A lean row carries no timeline, so the detail has to be fetched before the
      // menu can render anything.
      const detail = item?.timeline ? item : await fetchChaseDetail(item?.id);

      if (ignore || !detail) {
        return;
      }

      onToggleChaseMenu({
        id: detail?.id,
        data: {
          ...detail,
          policyReferenceNumber: detail?.policyReferenceNumber,
          apiValues: detail?.apiValues,
          isSameCustomerChase:
            detail?.isSameCustomerChase ?? isSameCustomerChase,
        },
        timeline: detail?.timeline,
        logs: detail?.logs,
      });
    };

    openOnlyChase().catch((error) => console.log(error));

    return () => {
      ignore = true;
    };
  }, [chase, isSameCustomerChase]);

  // ------------------------------ [ ON PAGE CHANGE GO BACK TO PAGE 1 ] -----------------

  useEffect(() => {
    setPage(1);
  }, [location]);

  return (
    <PageLayout
      pageTitle={
        myCutomers
          ? window.location.pathname.replace("-", " ").replace("/", "")
          : "Dashboard"
      }
      primaryAction={
        <div className="relative flex gap-5 max-sm:w-full">
          <div className="flex gap-5 max-sm:gap-3 w-full">
            <div className="relative">
              <DateRangePicker onChange={(e) => setDateRange(e)}>
                <div
                  onClick={onToggleRangePicker}
                  className="px-6 py-1 rounded-full appearance-none text-[16px] flex items-center justify-center h-[50px] bg-surface-elevated text-light-gray border border-edge hover:border-indigo-400/40 hover:text-text-primary transition-all duration-300 outline-none cursor-pointer"
                >
                  <FontAwesomeIcon icon={faCalendar} />
                </div>
              </DateRangePicker>
            </div>
            <button
              onClick={handleClick}
              className="flex gap-2 text-[16px] font-dmSans font-[600] items-center justify-center max-sm:flex-1 px-6 py-3 text-white bg-gradient-to-r from-accent to-accent-bright rounded-full border border-white/10 shadow-lg shadow-indigo-500/20 hover:shadow-glow-sm hover:-translate-y-0.5 transition-all duration-300"
            >
              <FontAwesomeIcon icon={faAdd} />
              New Chase
            </button>
          </div>
        </div>
      }
    >
      {window.location.pathname === "/my-customers" && (
        <div>
          <TopBarWidget />
        </div>
      )}

      <DebtSyncAlert className="mt-5 mr-1 max-sm:mr-0" />

      <div className="mt-5 mr-1 max-sm:mr-0 bg-surface border border-edge rounded-2xl shadow-card px-6 pt-6 pb-2 max-sm:px-3 max-sm:pt-4">
        <div className="flex w-full items-start justify-between gap-4 flex-wrap mb-5">
          <div className="min-w-0">
            <h1 className="text-[22px] font-bold text-text-primary leading-tight">
              Chase list
            </h1>
            {/* Scope and count in one line — states what the table below actually shows. */}
            <p className="mt-1 text-[13px] font-medium text-light-gray capitalize">
              {chaseStatus ? chaseStatus.replace("-", " ") : "All statuses"}
              {totalCount ? (
                <span className="normal-case">
                  {" · "}
                  {totalCount} {totalCount === 1 ? "chase" : "chases"}
                </span>
              ) : null}
            </p>
          </div>

          <div className="flex items-center gap-3 flex-wrap max-sm:w-full">
            {chaseStatus === "Chargebacks" ? (
              <div className="w-[120px] cursor-pointer">
                <CustomSelect
                  inputClass="rounded-full"
                  comboboxType="select"
                  options={chargeFilterOptions}
                  valueProperty="value"
                  labelProperty="label"
                  onSingleSelectChange={(e) => setChargeFilter(e.value)}
                  value={chargeFilterOptions?.find(
                    (cfo) => cfo.value === chargefilter
                  )}
                />
              </div>
            ) : null}

            <div className="h-10 min-w-[290px] max-sm:min-w-0 max-sm:flex-1 rounded-full flex bg-input-bg items-center border border-input-border focus-within:border-indigo-400/40 focus-within:ring-2 focus-within:ring-indigo-400/20 transition-all duration-200 px-4">
              <FontAwesomeIcon
                icon={faSearch}
                className="text-[13px] text-light-gray mr-2.5 shrink-0"
              />
              <input
                ref={searchRef}
                type="text"
                placeholder="Search name, email or reference"
                className="flex-grow bg-inherit text-[13px] text-text-primary placeholder:text-light-gray font-medium focus:outline-none disabled:opacity-50 disabled:cursor-wait"
                onChange={(e) => setQuery(e.target.value)}
                disabled={loading}
              />
            </div>
          </div>
        </div>

        <TableWrapper
          headingData={chaseHeadingData}
          isLoading={loading}
          totalCount={totalCount}
        >
          {chase?.map((item, index) => {
            return <ChaseRenderRow item={item} index={index} key={item.id} />;
          })}
        </TableWrapper>
        <PaginationMenu
          totalCount={totalCount}
          currentPage={page}
          pageSize={pageSize}
          onPageChange={OnChangeCurrentPage}
        />
      </div>
    </PageLayout>
  );
};

export default ChaseList;
